import React, { useState, useEffect, useMemo } from 'react';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchFilters, Header, DrawerHeader } from 'datastake-daf/dist/components';

import PMESclCreate from './create';
import getColumns from './columns';
import { connect } from 'react-redux';
import { actions } from '../../../../../store/reducers/scl';
import { findOptions, getDivergenceOrEqual } from '../../../../../helpers/StringHelper';
import { displayMessage } from '../../../../../helpers/Messages';
import { canViewFeatureAction } from '../../../../../constants/roles';
import Loading from '../../../../../components/Loading';
import { PROJECTS } from '../../../../../helpers/CanAccess';
import { filterParams, useFilters } from '../../../../../helpers/SubjectSearch';
import { filtersConfig, selectFiltersConfig } from '../../../../../components/Forms/locations/columns';
import { checkboxConfig } from '../../../../../components/Forms/locations/columns';
import Styles from '../../../../../components/Forms/locations/style';
import { getLocationFilterOptions } from '../../../../../components/Forms/locations/helper';
import { getProject } from '../../../../../services/BaseService';
import DafTable from '../../../../../components/Table/newTable';
import { useNavigate } from 'react-router-dom';
import { renderBreadCrumbs } from '../../../../../helpers/breadCrumbs';
import TablePagination from '../../../../../components/Table/pagination';
import { CREATE_DRAWER_WIDTH } from '../../../../../helpers/form';

const allStatuses = ['all', 'identified', 'inProgress', 'assessed', 'connected'];

function PMEScl({
  scl: { data, loading },
  SCLGet,
  SCLDataGet,
  ResetForm,
  authentication: { user, selectedProject, sourceId }
}) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [scls, setScls] = useState([]);
  const [selectOptions, setSelectOptions] = useState({
    status: allStatuses,
    country: [],
    type: [],
    materialHandled: [],
  });
  const [hasError, setHasError] = useState(false);

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
  } = useFilters({
    selectFiltersConfig,
    module: 'pme',
    view: 'scl',
    filtersConfig,
  });

  const columns = useMemo(() => getColumns(
    t,
    selectedProject !== PROJECTS.myData ? 'project' : 'show',
    sourceId,
    activeFilters
  ), [t, selectedProject, sourceId, activeFilters])

  useEffect(() => {
    const project = getProject();
    SCLGet(filterParams({
      activeFilters,
      project,
      sourceId,
    }));
  }, [user.language, selectedProject, sourceId, activeFilters])

  useEffect(() => {
    if (data.scls) {
      const { typeOfLocationOptions, sclStatuses, mineralOptions } = data.sclOptions || {};
      function mapLocations(data, options = {}) {
        return data.map((scl, i) => {
          const allLinkedItems = [].concat(...Object.keys(scl?.linking || {}).map(k => scl.linking[k]));
          let operator;
          if (scl.miningOperator && scl.miningOperator.stakeholderId) {
            allLinkedItems.forEach((d) => {
              if (d[scl.miningOperator.stakeholderId]) {
                operator = d[scl.miningOperator.stakeholderId];
                return;
              }
            });
          }
          const province = scl.linking && scl.linking.SCL ? scl.linking.SCL[scl.administrativeLevel1] : null;
          const territory = scl.linking && scl.linking.SCL ? scl.linking.SCL[scl.administrativeLevel2] : null;
          const author = scl.author ? (scl.author.name ? scl.author.name : `${scl.author.firstName} ${scl.author.lastName}`) : '';
          const childrens = (scl.children || []);
          const config = {
            id: scl.datastakeId ? scl.datastakeId : '',
            locationName: scl.locationName ? scl.locationName : '-',
            locationType: scl.category ? findOptions(scl.category, typeOfLocationOptions) : '-',
            category: scl.category,
            status: scl.status ? sclStatuses.find(s => s.value === scl.status) : '',
            territory: territory ? territory.name : '-',
            province: province ? province.name : '-',
            nrOfWorkers: scl.totalNoOfWorkers ? scl.totalNoOfWorkers : '-',
            organisationName: scl?.organisationName ? scl.organisationName : '-',
            operator: operator ? operator.name : '-',
            published: scl.published,
            authorId: scl.authorId,
            mineralTitleScope: (scl.materialHandled || []).map(val => {
              const type = (val || {}).type
              const v = (mineralOptions || []).find(m => m.value === type);
              return v ? v.label : type || '';
            }).join(', '),
            dbId: options.isChild ? `${scl.id}-${i}` : scl.id,
            author,
            updatedAt: scl.updatedAt,
            publishedAt: scl.publishedAt,
          };
          if (childrens.length > 1) {
            Object.assign(config, {
              children: mapLocations(childrens, { isChild: true }),
            });
            config.sourcesLen = config.children.length;
          }
          return getDivergenceOrEqual(config, ['nrOfWorkers', 'operator']);
        });
      }
      setSelectOptions((prev) => ({
        ...prev,
        ...getLocationFilterOptions(data.sclOptions),
        materialHandled: data.sclOptions.mineralOptions || [],
      }));
      setScls(mapLocations(data.scls));
    }
    if (typeof data.totalItems === 'number') {
      setPagination((prev) => ({ ...prev, total: data.totalItems }));
    }
  }, [data.scls]);

  const canEdit = canViewFeatureAction({ user, module: 'pme', feature: 'scl', action: 'create' }) && selectedProject === PROJECTS.myData;

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  const breadCrumbs = useMemo(() => {
    return renderBreadCrumbs({ t, view: 'scl', goTo, mod: 'pme' });
  }, [t, goTo]);

  return (
    <Styles className="semibold form-input-output">
      {loading && <Loading />}
      <Header
        title={t("Mine Sites")}
        breadcrumbs={breadCrumbs}
        actionButtons={canEdit ? [
          {
            label: t('New'),
            onClick: () => setEditVisible(true),
            type: 'primary',
          }
        ] : []}
      />
      <div className="column start table-content">
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            canClear={canClearSearch}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={scls}
            loading={loading}
            pagination={pagination}
            selectOptions={selectOptions}
            filtersConfig={selectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            defaultFilters={defaultFilters}
            setShowFilters={setShowFilters}
            rowKey="dbId"
            sourcesKey="authorId"
            onChange={onTableChange}
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </div>
      <Drawer
        destroyOnClose={true}
        title={<DrawerHeader title={t("Identification")} />}
        open={editVisible}
        maskClosable={false}
        onClose={() => {
          setEditVisible(false);
          const project = getProject();
          SCLGet(filterParams({
            activeFilters,
            project,
            sourceId,
          }));
          ResetForm('pme', ["scoping", "new"]);
        }}
        width={CREATE_DRAWER_WIDTH}
        bodyStyle={{ padding: 0 }}>
        {editVisible ? <PMESclCreate
          showSaveAndNext={false}
          namespace="SCL"
          module="pme"
          view={["scoping", "new"]}
          onSubmitted={(type, m, payload) => {
            displayMessage(type, m);
            setEditVisible(false);
            SCLGet()
            ResetForm('pme', ["scoping", "new"]);

            if (payload) {
              switch (payload.category) {
                case 'mineSite':
                  goTo(`/app/pme/edit/scl/${payload.datastakeId}`)
                  break;
                case 'village':
                  goTo(`/app/pme/edit/village/${payload.datastakeId}`)
                  break;
                default:
                  goTo(`/app/pme/edit/location/${payload.datastakeId}`)
              }
            }
          }}
          onCancel={() => setEditVisible(false)} /> : null}
      </Drawer>
    </Styles >
  )
};

const mstp = (state) => ({ scl: state.scl, authentication: state.authentication });

export default connect(mstp, actions)(PMEScl);
